// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ClearIcon from '@mui/icons-material/Clear';
import { createCancelableRemoteGet } from '../utils/remoteApiCancelable';
import axios from 'axios';
import { retryAsync } from '../utils/retry';
import { useTheme } from '../context/ThemeContext';
import { getGlobalBackoff } from '../utils/exponentialBackoff';

const LOG_LEVELS = {
  ALL: { label: 'All', color: 'default' },
  ERROR: {
    label: 'Error',
    color: 'error',
    keywords: ['error', 'fail', 'exception', 'critical'],
  },
  WARNING: {
    label: 'Warning',
    color: 'warning',
    keywords: ['warn', 'warning', 'caution'],
  },
  INFO: { label: 'Info', color: 'info', keywords: ['info', 'information'] },
  DEBUG: {
    label: 'Debug',
    color: 'success',
    keywords: ['debug', 'trace', 'verbose'],
  },
};

const StargateSystemLogs = ({ ip, apiKey }) => {
  const { isDarkMode } = useTheme();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [logLevel, setLogLevel] = useState('ALL');
  const [autoRefresh, setAutoRefresh] = useState(true); // Default to ON
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const logContainerRef = useRef(null);
  const intervalRef = useRef(null);
  const activeRequestsRef = useRef([]);
  const [userScrolled, setUserScrolled] = useState(false); // Track if user manually scrolled
  const [isDragging, setIsDragging] = useState(false); // Track if user is dragging scrollbar
  const backoff = getGlobalBackoff();

  // Base polling interval (will be adjusted by backoff on errors)
  const BASE_POLLING_INTERVAL = 5000; // 5 seconds

  useEffect(() => {
    fetchLogs(true);
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      // Cancel all active requests when component unmounts
      activeRequestsRef.current.forEach((req) => {
        if (req && req.cancel) {
          req.cancel();
        }
      });
      activeRequestsRef.current = [];
      // Reset backoff state for this component
      backoff.resetKey(`system-logs-${ip}`);
    };
  }, [ip, apiKey]);

  useEffect(() => {
    if (autoRefresh) {
      const scheduleNextPoll = () => {
        if (intervalRef.current) clearTimeout(intervalRef.current);

        // Get backoff delay for system logs polling
        const backoffKey = `system-logs-${ip}`;
        const delay = backoff.getDelay(backoffKey) || BASE_POLLING_INTERVAL;

        intervalRef.current = setTimeout(() => {
          fetchLogs(false).finally(() => {
            if (autoRefresh) {
              scheduleNextPoll(); // Schedule next poll only if still auto-refreshing
            }
          });
        }, delay);
      };

      scheduleNextPoll();
    } else {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [autoRefresh, ip, apiKey]);

  const fetchLogs = async (isInitial = false) => {
    if (isInitial || !hasLoadedOnce) setLoading(true);
    setError(null);

    // Clear previous requests
    activeRequestsRef.current.forEach((req) => {
      if (req && req.cancel) {
        req.cancel();
      }
    });
    activeRequestsRef.current = [];

    try {
      const retryFetch = () =>
        retryAsync(async () => {
          // Create cancelable request
          const logsReq = createCancelableRemoteGet(ip, apiKey, '/api/system/logs');
          activeRequestsRef.current.push(logsReq);

          const data = await logsReq.request;

          // Extract .logs if present, otherwise assume it's the logs directly
          return data?.logs ?? data;
        }, 3);

      const result = await retryFetch();

      // Parse logs if they're a string
      let parsedLogs = [];
      if (typeof result === 'string') {
        parsedLogs = result
          .split('\n')
          .filter((line) => line.trim())
          .map((line, index) => ({
            id: index,
            timestamp: new Date().toISOString(),
            message: line,
            level: detectLogLevel(line),
          }));
      } else if (Array.isArray(result)) {
        parsedLogs = result.map((log, index) => ({
          id: index,
          timestamp: log.timestamp || new Date().toISOString(),
          message: log.message || log,
          level: log.level || detectLogLevel(log.message || log),
        }));
      }

      setLogs(parsedLogs);
      applyFilters(parsedLogs, filter, logLevel);
      if (!hasLoadedOnce) setHasLoadedOnce(true);

      // Auto-scroll to bottom on new logs only if user hasn't manually scrolled and isn't dragging
      if (logContainerRef.current && autoRefresh && !userScrolled && !isDragging) {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      }

      // Record success with backoff
      backoff.recordSuccess(`system-logs-${ip}`);
    } catch (e) {
      // Don't log or show error for canceled requests
      if (axios.isCancel(e)) {
        console.log('[StargateSystemLogs] Request canceled');
        return;
      }

      // Record failure with exponential backoff
      const nextDelay = backoff.recordFailure(`system-logs-${ip}`, e);

      // Only show error if not throttled
      if (!backoff.shouldThrottleError()) {
        console.warn(
          `[SystemLogs] Failed to fetch logs, next retry in ${Math.round(nextDelay / 1000)}s`
        );
        setError('Failed to fetch system logs');
        setSnackbar({
          open: true,
          message: 'Failed to fetch logs',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const detectLogLevel = (message) => {
    const lowerMessage = message.toLowerCase();
    for (const [level, config] of Object.entries(LOG_LEVELS)) {
      if (level === 'ALL') continue;
      if (config.keywords.some((keyword) => lowerMessage.includes(keyword))) {
        return level;
      }
    }
    return 'INFO';
  };

  const applyFilters = (logsToFilter, searchFilter, levelFilter) => {
    let filtered = logsToFilter;

    // Apply level filter
    if (levelFilter !== 'ALL') {
      filtered = filtered.filter((log) => log.level === levelFilter);
    }

    // Apply search filter
    if (searchFilter) {
      filtered = filtered.filter((log) =>
        log.message.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  };

  useEffect(() => {
    applyFilters(logs, filter, logLevel);
  }, [filter, logLevel, logs]);

  // Add scroll event listener to detect user interaction
  useEffect(() => {
    const container = logContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10; // 10px threshold

      // If user scrolled away from bottom, set userScrolled to true
      if (!isAtBottom) {
        setUserScrolled(true);
      } else {
        // If user scrolled back to bottom, reset the flag
        setUserScrolled(false);
      }
    };

    const handleMouseDown = () => {
      setIsDragging(true);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleMouseLeave = () => {
      setIsDragging(false);
    };

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleClearLogs = () => {
    setLogs([]);
    setFilteredLogs([]);
    setSnackbar({ open: true, message: 'Logs cleared', severity: 'info' });
  };

  const handleDownloadLogs = () => {
    const content = filteredLogs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.message}`)
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stargate-logs-${new Date().toISOString()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: 'Logs downloaded',
      severity: 'success',
    });
  };

  const getLogChipColor = (level) => {
    return LOG_LEVELS[level]?.color || 'default';
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, fontSize: 20 }}>
            System Logs
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`${filteredLogs.length} logs`}
              size="small"
              sx={{
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              }}
            />
            <IconButton onClick={handleClearLogs} size="small" title="Clear logs">
              <ClearIcon />
            </IconButton>
            <IconButton onClick={handleDownloadLogs} size="small" title="Download logs">
              <CloudDownloadIcon />
            </IconButton>
            <Button
              onClick={() => fetchLogs()}
              size="small"
              sx={{
                minWidth: 0,
                color: 'text.primary',
                background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                borderRadius: 2,
                '&:hover': {
                  background: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
                },
              }}
              disabled={loading}
            >
              <RefreshIcon />
            </Button>
          </Box>
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
          '& .MuiCardHeader-action': {
            margin: 0,
            alignSelf: 'center',
          },
        }}
      />
      <CardContent>
        {/* Filters */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder="Search logs..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <ToggleButtonGroup
            value={logLevel}
            exclusive
            onChange={(e, newLevel) => newLevel && setLogLevel(newLevel)}
            size="small"
          >
            {Object.entries(LOG_LEVELS).map(([level, config]) => (
              <ToggleButton key={level} value={level}>
                <Chip
                  label={config.label}
                  size="small"
                  color={config.color}
                  sx={{ cursor: 'pointer' }}
                />
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButton
            value="check"
            selected={autoRefresh}
            onChange={() => setAutoRefresh(!autoRefresh)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </ToggleButton>
        </Box>

        {/* Logs Display */}
        <Box
          ref={logContainerRef}
          sx={{
            bgcolor: isDarkMode ? 'background.default' : '#f5f7fa',
            borderRadius: 2,
            p: 2,
            height: 400,
            overflowY: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            border: '1px solid',
            borderColor: 'divider',
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: isDarkMode ? 'rgba(138, 148, 168, 0.04)' : 'rgba(138, 148, 168, 0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: isDarkMode ? 'rgba(138, 148, 168, 0.3)' : '#999',
              borderRadius: 4,
            },
          }}
        >
          {loading && !hasLoadedOnce ? (
            <Typography sx={{ color: 'text.secondary' }}>Loading logs...</Typography>
          ) : error ? (
            <Typography sx={{ color: '#f44336' }}>{error}</Typography>
          ) : filteredLogs.length === 0 ? (
            <Typography sx={{ color: 'text.secondary' }}>No logs found</Typography>
          ) : (
            filteredLogs.map((log) => (
              <Box
                key={log.id}
                sx={{
                  mb: 0.5,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <Chip
                  label={log.level}
                  size="small"
                  color={getLogChipColor(log.level)}
                  sx={{
                    fontSize: '0.75rem',
                    height: 20,
                    minWidth: 60,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
                <Typography
                  sx={{
                    color:
                      log.level === 'ERROR'
                        ? '#f44336'
                        : log.level === 'WARNING'
                          ? '#ff9800'
                          : 'text.primary',
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {log.message}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default StargateSystemLogs;
